import { getPedidosRepository, CreatePedidoDto, PedidoResponse } from '../repositories';
export type { CreatePedidoDto, PedidoResponse } from '../repositories';
import { ApiError } from './types';
import { generateUUID } from '../utils/uuid';

export class PedidosService {
  private static get repository() {
    return getPedidosRepository();
  }

  /**
   * Cria um pré-pedido na landing page
   * @param pedidoData - Dados do pedido (agendamentos, tipo de pagamento)
   * @returns Promise com resposta do pedido criado
   */
  static async createPedido(pedidoData: CreatePedidoDto): Promise<PedidoResponse> {
    // Gerar UUID único se não foi fornecido
    if (!pedidoData.client_request_id) {
      pedidoData.client_request_id = generateUUID();
    }

    // Salvar UUID no localStorage para recuperação
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastClientRequestId', pedidoData.client_request_id);
    }

    try {
      return await this.repository.createPedido(pedidoData);
    } catch (error: any) {
      // Em caso de erro, tentar buscar pedido pelo client_request_id
      if (pedidoData.client_request_id) {
        try {
          const pedidoExistente = await this.repository.getPedidoByClientRequestId(
            pedidoData.client_request_id
          );
          if (pedidoExistente) {
            console.log('✅ Pedido já foi criado anteriormente:', pedidoExistente);
            return {
              pedido_id: pedidoExistente.id,
              codigo_pedido: pedidoExistente.codigo_pedido,
              valor_total: Number(pedidoExistente.valor_total),
              desconto: Number(pedidoExistente.desconto || 0),
              valor_final: Number(pedidoExistente.valor),
              status: pedidoExistente.status?.valor || 'pendente',
              valor_irrf: pedidoExistente.valor_irrf ? Number(pedidoExistente.valor_irrf) : undefined,
              valor_csll: pedidoExistente.valor_csll ? Number(pedidoExistente.valor_csll) : undefined,
              valor_pis: pedidoExistente.valor_pis ? Number(pedidoExistente.valor_pis) : undefined,
              valor_cofins: pedidoExistente.valor_cofins ? Number(pedidoExistente.valor_cofins) : undefined,
            };
          }
        } catch (searchError) {
          // Se não encontrar, lançar erro original
          console.error('❌ Erro ao buscar pedido por client_request_id:', searchError);
        }
      }
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca pedido pendente da empresa
   * @param empresaId - ID da empresa (opcional, mas recomendado)
   * @returns Promise com pedido pendente ou null
   */
  static async getPendingPedido(empresaId?: number): Promise<any | null> {
    try {
      return await this.repository.getPendingPedido(empresaId);
    } catch (error: any) {
      // Se não houver pedido pendente, retorna null
      if (error.response?.status === 404) {
        return null;
      }
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca detalhes de um pedido por ID
   * @param id - ID do pedido
   * @returns Promise com detalhes do pedido
   */
  static async getPedidoById(id: number): Promise<any> {
    try {
      return await this.repository.getPedidoById(id);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca detalhes de um pedido por codigo_pedido
   * @param codigo - Código do pedido
   * @returns Promise com detalhes do pedido
   */
  static async getPedidoByCodigo(codigo: string): Promise<any> {
    try {
      return await this.repository.getPedidoByCodigo(codigo);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Atualiza um pedido pendente com novos agendamentos
   * @param pedidoId - ID do pedido a ser atualizado
   * @param pedidoData - Novos dados do pedido (agendamentos)
   * @returns Promise com resposta do pedido atualizado
   */
  static async updatePedido(pedidoId: number, pedidoData: Partial<CreatePedidoDto>): Promise<PedidoResponse> {
    try {
      return await this.repository.updatePedido(pedidoId, pedidoData);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Atualiza o endereço associado ao pedido
   */
  static async updatePedidoEndereco(pedidoId: number, enderecoData: any): Promise<any> {
    try {
      return await this.repository.updatePedidoEndereco(pedidoId, enderecoData);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Busca colaboradores e exames por pedido_id
   * @param pedidoId - ID do pedido
   * @returns Promise com colaboradores e exames do pedido
   */
  static async getColaboradoresExamesPorPedido(pedidoId: number): Promise<any> {
    try {
      return await this.repository.getColaboradoresExamesPorPedido(pedidoId);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Trata erros da API
   */
  private static handleApiError(error: any): ApiError {
    if (error.response?.data) {
      // Capturar mensagens de validação do NestJS
      const validationMessages = error.response.data.message;
      let message = error.message || 'Erro na requisição';

      // Se for array de mensagens de validação, formatar
      if (Array.isArray(validationMessages)) {
        message = validationMessages.join(', ');
      } else if (typeof validationMessages === 'string') {
        message = validationMessages;
      }

      // Log detalhado para debug
      console.error('❌ Erro da API:', {
        status: error.response.status,
        message: message,
        data: error.response.data,
        validationErrors: error.response.data.message,
      });

      return {
        message: message,
        statusCode: error.response.status || error.statusCode || 500,
        error: error.response.data.error || error.error || 'Unknown Error',
      };
    }

    if (error.message) {
      return {
        message: error.message,
        statusCode: 500,
        error: 'Network Error',
      };
    }

    return {
      message: 'Erro de conexão com o servidor',
      statusCode: 500,
      error: 'Network Error',
    };
  }
}

